/**
 * Ownership rules for the table shimmer flag.
 *
 * The flag lives once in `state.table` and every mounted table reads it,
 * so two tables on screen at the same time — a patient picker inside the
 * Start Program panel sitting over the Programs list — used to shimmer
 * together, and whichever finished loading first cleared it for both.
 * Arming now records WHO armed it.
 *
 * Deliberately free of imports: `tablesSlice` cannot be unit-tested
 * because it pulls the API barrel (and MUI with it), so the rules that
 * are worth testing live here instead.
 */

/**
 * Normalize an `updateCanShowShimmer` payload.
 *
 * Accepts the legacy boolean, which means "every table" and is what the
 * bulk-action features dispatch, or `{ value, tableKey }` from a table
 * acting for itself.
 *
 * @param {boolean | {value: boolean, tableKey?: string | null}} payload
 * @returns {{value: boolean, tableKey: string | null}}
 */
export const readShimmerPayload = payload => {
  if (typeof payload === 'object' && payload !== null) {
    return { value: !!payload.value, tableKey: payload.tableKey ?? null };
  }
  return { value: !!payload, tableKey: null };
};

/**
 * May `tableKey` clear a flag currently owned by `owner`?
 *
 * An unowned flag (armed for everyone) may be cleared by anyone, and a
 * keyless request clears whatever is set. Otherwise only the owner may.
 *
 * @param {string | null} owner
 * @param {string | null} tableKey
 * @returns {boolean}
 */
export const canDisarmShimmer = (owner, tableKey) =>
  owner === null || tableKey === null || owner === tableKey;

/**
 * Should this table paint skeletons?
 *
 * @param {boolean} canShowShimmer
 * @param {string | null} owner the table that armed the flag
 * @param {string | undefined} tableKey the table asking
 * @returns {boolean}
 */
export const isShimmerForTable = (canShowShimmer, owner, tableKey) =>
  !!canShowShimmer && (owner === null || owner === tableKey);
